/**
 * §03「つながりの構造」レベルB（個人ネットワーク）プロトタイプ表示。
 *
 * 部署ごとに「相談ネットワーク」をハブ図で描画し、キーパーソン（相談が集まる人）を強調。
 * 構造を 三角形（分散・健全）/ V字（一極集中・属人化）/ スポーク に分類して示す。
 * 実名表示は showRealNames フラグで出し分け（会社単位設定の想定）。
 */
import React, { useState } from 'react';
import { Box, Typography, Paper, Stack, Chip, Collapse } from '@mui/material';
import type { ConsultNetwork, DeptConsultStat, ConsultMember } from '../utils/dummyConsultNetwork';

const SHAPE_META: Record<DeptConsultStat['shape'], { label: string; color: string; mark: string }> = {
  triangle: { label: '複数キーパーソン（多極・支え合い）', color: '#22A06B', mark: '△' },
  vee: { label: 'V字（一極集中・属人化）', color: '#C0392B', mark: 'V' },
  spoke: { label: 'スポーク（相談が薄い）', color: '#B8860B', mark: '─' },
};

function display(member: ConsultMember, showRealNames: boolean): string {
  return showRealNames ? `${member.name}` : member.anonLabel;
}

/** 部署サマリ文を実名/匿名に応じて生成（util側の固定文は実名を含むため使わない） */
function deptSummary(stat: DeptConsultStat, showRealNames: boolean): string {
  const kpLabels = stat.keypersonIds
    .map((id) => stat.members.find((m) => m.id === id))
    .filter((m): m is ConsultMember => Boolean(m))
    .map((m) => display(m, showRealNames));
  if (stat.shape === 'vee') {
    return `${kpLabels[0]}に相談が集中（${stat.maxInDegree}人が依存）。${stat.dept}は属人化（V字）のリスクがあり、その人が抜けると回りにくくなります。`;
  }
  if (stat.shape === 'triangle') {
    return `${kpLabels.join('・')}に相談が集まっています（複数のキーパーソン）。一極集中ではなく多極で、1人依存よりは安定しています。ただしこの数名に負荷が偏ります。`;
  }
  return `${stat.dept}は相談関係が薄く、連携が限定的です。相談し合う相手を増やすと安定します。`;
}

/** 部署の相談ネットワークをハブ図で描画 */
const DeptHubGraph: React.FC<{ stat: DeptConsultStat; showRealNames: boolean }> = ({
  stat,
  showRealNames,
}) => {
  const W = 420;
  const H = 320;
  const cx = W / 2;
  const cy = H / 2;
  const R = 130; // 外周(その他メンバー)の配置半径。ノード間隔を広げ可読性を確保
  const Rin = 58; // キーパーソンが複数のときの内側配置半径
  const kpSet = new Set(stat.keypersonIds);
  const kps = stat.members.filter((m) => kpSet.has(m.id));
  const others = stat.members.filter((m) => !kpSet.has(m.id));

  // 座標: キーパーソン1人=中心 / 複数=内側の小円 / その他=外周
  const pos = new Map<string, { x: number; y: number }>();
  if (kps.length === 1) {
    pos.set(kps[0].id, { x: cx, y: cy });
  } else {
    kps.forEach((m, i) => {
      const a = (2 * Math.PI * i) / Math.max(1, kps.length) - Math.PI / 2;
      pos.set(m.id, { x: cx + Rin * Math.cos(a), y: cy + Rin * Math.sin(a) });
    });
  }
  others.forEach((m, i) => {
    const a = (2 * Math.PI * i) / Math.max(1, others.length) - Math.PI / 2;
    pos.set(m.id, { x: cx + R * Math.cos(a), y: cy + R * Math.sin(a) });
  });

  const meta = SHAPE_META[stat.shape];

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${W} ${H}`}
      style={{ display: 'block', maxWidth: W, margin: '0 auto' }}
      role="img"
      aria-label={`${stat.dept}の相談ネットワーク`}
    >
      {/* エッジ */}
      {stat.edges.map((e, i) => {
        const a = pos.get(e.from);
        const b = pos.get(e.to);
        if (!a || !b) return null;
        const toKp = kpSet.has(e.to);
        return (
          <line
            key={i}
            x1={a.x}
            y1={a.y}
            x2={b.x}
            y2={b.y}
            stroke={toKp ? meta.color : '#C9D2DC'}
            strokeWidth={toKp ? 1.8 : 1}
            strokeOpacity={toKp ? 0.8 : 0.5}
          />
        );
      })}
      {/* ノード */}
      {stat.members.map((m) => {
        const p = pos.get(m.id)!;
        const isKp = kpSet.has(m.id);
        const inDeg = stat.inDegree[m.id] ?? 0;
        const r = isKp ? 22 : 13;
        return (
          <g key={m.id}>
            <circle
              cx={p.x}
              cy={p.y}
              r={r}
              fill={isKp ? meta.color : '#EEF2F7'}
              stroke={isKp ? meta.color : '#AEBac6'}
              strokeWidth={isKp ? 2 : 1}
            />
            {/* 氏名は円を横切って全文表示（円サイズは維持）。はみ出す部分も読めるよう白フチ(ハロー)を付与 */}
            <text
              x={p.x}
              y={p.y + (isKp ? 1 : 4)}
              textAnchor="middle"
              fontSize={isKp ? 11 : 10}
              fontWeight={isKp ? 700 : 500}
              fill="#1F2A37"
              stroke="#ffffff"
              strokeWidth={3}
              strokeLinejoin="round"
              style={{ paintOrder: 'stroke' }}
            >
              {display(m, showRealNames)}
            </text>
            {isKp && (
              <text x={p.x} y={p.y - r - 4} textAnchor="middle" fontSize={10} fill={meta.color} fontWeight={700}>
                キーパーソン({inDeg})
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

export const ConnectionStructureLevelB: React.FC<{
  network: ConsultNetwork;
  showRealNames: boolean;
}> = ({ network, showRealNames }) => {
  return (
    <Box>
      {/* 全社キーパーソン一覧 */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#FAFBFC' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          キーパーソン（相談が集まる人）
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {network.topKeypersons.map((k) => (
            <Chip
              key={k.member.id}
              label={`${display(k.member, showRealNames)}・${k.member.dept}（${k.inDegree}人）`}
              size="small"
              color={k.shape === 'vee' ? 'error' : 'default'}
              variant={k.shape === 'vee' ? 'filled' : 'outlined'}
            />
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          赤＝相談が一極集中（V字・属人化リスク）。その人が抜けるとチームが回りにくくなります。
        </Typography>
      </Paper>

      {/* 部署別ネットワーク */}
      <Stack spacing={2}>
        {network.depts.map((d) => {
          const meta = SHAPE_META[d.shape];
          return (
            <Paper key={d.dept} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {d.dept}
                </Typography>
                <Chip
                  label={`${meta.mark} ${meta.label}`}
                  size="small"
                  sx={{ bgcolor: meta.color, color: '#fff', fontWeight: 700 }}
                />
              </Stack>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <DeptHubGraph stat={d} showRealNames={showRealNames} />
              </Box>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {deptSummary(d, showRealNames)}
              </Typography>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
};

/**
 * §03用アコーディオン表示（本番§03に組み込む版）。
 * 既定は「部門名＋評価ピル」だけ（image2）。部門をクリックすると当該部門のみ展開し、
 * 相談ネットワーク図＋要約（image3）を表示する。基本方針2により実名は表示しない（匿名ラベル）。
 */
export const ConnectionStructureLevelBAccordion: React.FC<{
  network: ConsultNetwork;
  /** 実名表示（既定 false＝匿名。基本方針2に従い当面は常に false 運用） */
  showRealNames?: boolean;
}> = ({ network, showRealNames = false }) => {
  const [openDept, setOpenDept] = useState<string | null>(null);
  return (
    <Stack spacing={1.5}>
      {network.depts.map((d) => {
        const meta = SHAPE_META[d.shape];
        const open = openDept === d.dept;
        const toggle = () => setOpenDept(open ? null : d.dept);
        return (
          <Paper key={d.dept} variant="outlined" sx={{ overflow: 'hidden' }}>
            {/* image2: クリック可能な見出し行（部門名＋評価ピル） */}
            <Box
              onClick={toggle}
              role="button"
              tabIndex={0}
              aria-expanded={open}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggle();
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 2,
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': { bgcolor: '#F7F9FB' },
              }}
            >
              <Box
                component="span"
                sx={{
                  color: '#9aa3ad',
                  fontSize: 14,
                  lineHeight: 1,
                  transform: open ? 'rotate(90deg)' : 'none',
                  transition: 'transform .15s',
                }}
              >
                ▸
              </Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ minWidth: 84 }}>
                {d.dept}
              </Typography>
              <Chip
                label={`${meta.mark} ${meta.label}`}
                size="small"
                sx={{ bgcolor: meta.color, color: '#fff', fontWeight: 700 }}
              />
            </Box>
            {/* image3: 展開時のネットワーク図＋要約 */}
            <Collapse in={open} unmountOnExit>
              <Box sx={{ px: 2, pb: 2, borderTop: '1px solid #EEF2F7' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                  <DeptHubGraph stat={d} showRealNames={showRealNames} />
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {deptSummary(d, showRealNames)}
                </Typography>
              </Box>
            </Collapse>
          </Paper>
        );
      })}
    </Stack>
  );
};
